import type { CSSProperties } from 'react'
import { Link } from 'react-router-dom'
import { BandReveal, HeroReveal, InteractiveDots, ScrollReveal, StaggerContainer, StaggerItem, TextReveal } from '@/components/motion'
import { RotatedProcessCards, StickyFeatures } from '@/components/landing/service'
import { halftone, type BrandColor } from '@/components/landing/service/halftone'
import { PromptStage } from './fragments'
import { HighlightHeading, PlatformCtaBand, PlatformFaq, TrialButton, ink, type LayoutProps } from './shared'

const STEP_ACCENTS: BrandColor[] = ['purple', 'blue', 'pink']

/**
 * AI generation ("dark-product"): a black product hero with the real
 * workflow-builder screenshot in a square frame bleeding off the right edge;
 * pinned use cases as prompts streaming their answers; capabilities as
 * rotated process cards; a pink numbers band; FAQ; purple CTA.
 */
export function AiGenerationLayout({ feature }: LayoutProps) {
  return (
    <>
      {/* 1. Dark product hero */}
      <section aria-labelledby="ai-hero-title" className="relative overflow-hidden bg-[#0a0a0a] text-white">
        <div className="mx-auto grid max-w-[120rem] items-center lg:grid-cols-12">
          <div className="px-[clamp(1.5rem,2vw,2rem)] pb-14 pt-16 md:pt-24 lg:col-span-6 lg:py-24 lg:pr-12">
            <HeroReveal delay={0}>
              <p className="landing-text-display-lg">
                <span className="inline-block -rotate-2 bg-[var(--color-landing-accent)] px-[0.3em] text-white">
                  {feature.shortLabel}.
                </span>
              </p>
            </HeroReveal>
            <HeroReveal delay={0.1}>
              <h1 id="ai-hero-title" className="landing-text-hero-sm mt-6 text-white">
                <span className="block">{feature.heroLines[0]}</span>{' '}
                <span className="block text-[#ff00f2]">{feature.heroLines[1]}</span>
              </h1>
            </HeroReveal>
            <HeroReveal delay={0.2}>
              <p className="landing-text-body-lg mt-8 max-w-[52ch] text-pretty border-l border-white/25 pl-5 text-white/70 md:text-xl">
                {feature.subheadline}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <TrialButton surface="dark" />
                <Link to="/contact" className="landing-cta landing-cta--outline landing-cta--lg landing-cta--on-color landing-btn-sweep">
                  Book a demo
                </Link>
              </div>
            </HeroReveal>
            <HeroReveal delay={0.3}>
              <dl className="mt-14 grid grid-cols-3 gap-4 border-t border-white/15 pt-8 md:gap-8">
                {feature.heroStats.map((stat) => (
                  <div key={stat.label} className="flex min-w-0 flex-col-reverse justify-end">
                    <dt className="mt-2 text-sm leading-snug text-white/60 md:text-base">{stat.label}</dt>
                    <dd className="font-[family-name:var(--font-landing-display)] text-[clamp(1.5rem,3vw,2.5rem)] uppercase leading-[1.3] tracking-[-0.02em] text-white">
                      {stat.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </HeroReveal>
          </div>

          {/* Square frame, bleeding off the right edge; the screenshot runs past it */}
          <HeroReveal delay={0.25} distance={40} className="lg:col-span-6">
            <div className="relative aspect-square overflow-hidden bg-[var(--color-landing-secondary)]">
              <span aria-hidden className="absolute inset-0" style={halftone(0.28, 7)} />
              <span aria-hidden className="absolute bottom-0 left-0 h-[34%] w-[34%] bg-[#ff00f2]" style={halftone(0.3, 6)} />
              <img
                src={feature.heroImage}
                alt={feature.heroImageAlt || `${feature.label} screenshot`}
                className="absolute left-[9%] top-[12%] w-[150%] max-w-none border border-white/30 bg-white shadow-[0_50px_90px_-40px_rgba(0,0,0,0.8)]"
                width={1305}
                height={875}
              />
            </div>
          </HeroReveal>
        </div>
      </section>

      {/* 2. Use cases: each prompt streams its answer */}
      <section aria-labelledby="ai-usecases-title" className="landing-section">
        <div className="landing-container mb-12 lg:mb-0">
          <TextReveal as="h2" className={`landing-text-display-xl ${ink}`}>
            <span id="ai-usecases-title">
              Prompt in, <span className="landing-highlight landing-highlight--secondary">draft out.</span>
            </span>
          </TextReveal>
        </div>
        <StickyFeatures
          accent="pink"
          steps={feature.useCases.map((useCase, i) => ({
            title: useCase.title,
            description: useCase.description,
            extra: (
              <span className="inline-block bg-[var(--color-landing-primary)] px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-white dark:bg-white dark:text-[var(--color-landing-primary)]">
                {useCase.industry}
              </span>
            ),
            visual: (active: boolean) => (
              <PromptStage
                demo={useCase.demo ?? { ask: useCase.title, answer: useCase.description }}
                active={active}
                accent={STEP_ACCENTS[i % STEP_ACCENTS.length]!}
                app="word"
                label={useCase.title}
              />
            ),
          }))}
        />
      </section>

      {/* 3. Capabilities as rotated process cards */}
      <section aria-labelledby="ai-capabilities-title" className="landing-section overflow-x-clip bg-[var(--color-landing-gray-50)] dark:bg-[#12121a]">
        <div className="landing-container">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)] lg:items-end lg:gap-20">
            <HighlightHeading id="ai-capabilities-title" text={feature.valueProposition.title} color="blue" />
            <ScrollReveal direction="none" delay={0.15}>
              <p className="landing-text-body-lg max-w-[52ch] border-l border-[var(--color-landing-gray-300)] pl-5 text-[var(--color-landing-gray-600)] dark:border-[var(--color-landing-gray-700)] dark:text-[var(--color-landing-gray-400)]">
                {feature.valueProposition.description}
              </p>
            </ScrollReveal>
          </div>
          <RotatedProcessCards
            className="mt-16 lg:mt-12"
            cards={feature.capabilities.map((c) => ({ title: c.title, description: c.description }))}
          />
        </div>
      </section>

      {/* 4. Benefits as a pink numbers band */}
      <BandReveal>
        <section aria-labelledby="ai-impact-title" className="landing-section-lg relative overflow-hidden bg-[#ff00f2] text-[#0a0a0a]">
          <InteractiveDots />
          <div className="landing-container relative z-10">
            <TextReveal as="h2" className="landing-text-display-xl text-[#0a0a0a]">
              <span id="ai-impact-title">Business impact.</span>
            </TextReveal>
            <StaggerContainer
              amount={0.1}
              className="mt-14 grid gap-y-12 sm:grid-cols-2 lg:mt-20 lg:grid-cols-4"
              style={{ '--len': Math.max(2, ...feature.benefits.map((b) => (b.stat ?? '').length)) } as CSSProperties}
            >
              {feature.benefits.map((b) => (
                <StaggerItem key={b.title} className="border-t-2 border-[#0a0a0a] pt-6 sm:pr-8">
                  {b.stat && (
                    <p className="whitespace-nowrap font-[family-name:var(--font-landing-display)] text-[clamp(2rem,calc(70vw/var(--len)),4.75rem)] sm:text-[clamp(2rem,calc(36vw/var(--len)),4.75rem)] lg:text-[clamp(1.75rem,calc(19vw/var(--len)),4.75rem)] uppercase leading-[1.25] tracking-[-0.03em]">
                      {b.stat}
                    </p>
                  )}
                  <h3 className="mt-4 font-[family-name:var(--font-landing-subdisplay)] text-xl font-bold uppercase tracking-[-0.01em] text-[#0a0a0a]">
                    {b.title}
                  </h3>
                  <p className="landing-text-body-sm mt-2 max-w-[34ch] text-[#0a0a0a]/75">{b.description}</p>
                </StaggerItem>
              ))}
            </StaggerContainer>
          </div>
        </section>
      </BandReveal>

      <PlatformFaq feature={feature} />
      <PlatformCtaBand feature={feature} tone="purple" />
    </>
  )
}
